import { useEffect, useRef } from "react";
import { useNavigate } from "react-router";
import { useServices } from "@/app/use-services";
import { useSession } from "@/lib/auth/use-session";
import { destinationAfterSignIn, PATHS } from "@/features/onboarding/destinations";
import { Working } from "@/ui/Working";

/**
 * Where Google sends the person back. Supabase reads the code out of the URL
 * on its own; this screen waits for the session to appear and then lands the
 * person exactly where the email route lands them.
 */
export function AuthCallback() {
  const { session, draft, analytics } = useServices();
  const snapshot = useSession(session);
  const navigate = useNavigate();
  const reported = useRef(false);

  useEffect(() => {
    // Google is the only sign-in that comes back through here.
    const report = (ok: boolean) => {
      if (reported.current) return;
      reported.current = true;
      analytics.track(ok ? { name: "signed in", method: "google" } : { name: "sign-in failed", method: "google", step: "callback" });
    };
    if (snapshot.status === "signed_in") {
      report(true);
      navigate(destinationAfterSignIn(draft.getSnapshot()), { replace: true });
    } else if (snapshot.status === "signed_out") {
      // Only once the URL has been consumed: Supabase reports signed_out
      // before it has exchanged the code, so give it a beat.
      const timer = setTimeout(() => {
        if (session.getSnapshot().status !== "signed_out") return;
        report(false);
        navigate(PATHS.signIn, { replace: true });
      }, 1500);
      return () => clearTimeout(timer);
    }
    return undefined;
  }, [snapshot.status, navigate, draft, session, analytics]);

  return <Working message="Signing you in." />;
}
